import axios from 'axios';
import { RoutePaths } from '../../routes/RoutePaths';
import { store } from '../';
import {
    getAccessToken,
    getRefreshToken,
    saveTokens,
    isToolsPage,
} from '../base-utils';

// Add fallback values or throw error if env vars are missing
const BACKEND_IP = import.meta.env.VITE_BACKEND_IP || '';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

if (!BACKEND_IP) {
    console.error('VITE_BACKEND_IP is not defined');
}

const instance = axios.create({
    baseURL: `${BACKEND_IP}${API_BASE_URL}`,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add a request interceptor to add JWT token to requests
instance.interceptors.request.use(
    (config) => {
        // Axios 1.x JSON-serializes FormData when Content-Type is application/json,
        // which turns File objects into `{ preview: "blob:..." }`. Drop the default
        // JSON header so the browser can set multipart/form-data with a boundary.
        if (config.data instanceof FormData) {
            const headers = config.headers;
            if (headers && typeof headers.delete === 'function') {
                headers.delete('Content-Type');
            } else if (headers) {
                delete headers['Content-Type'];
                delete headers['content-type'];
            }
        }

        const token = getAccessToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add a response interceptor to handle token refresh
let isRefreshing = false;
const refreshSubscribers: Array<(token: unknown) => void> = [];

const subscribeTokenRefresh = (callback: (value: unknown) => void) => {
    refreshSubscribers.push(callback);
};

const onTokenRefreshed = (token: unknown) => {
    refreshSubscribers.map((callback) => callback(token));
};

instance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const refreshUrl = isToolsPage()
            ? '/tools/token/refresh/'
            : '/token/refresh/';
        const originalRequest = error.config;
        const isRefreshTokenFailed =
            error.response?.config?.url === refreshUrl &&
            [400, 401].includes(error.response.status);
        if (
            (isRefreshTokenFailed ||
                (error.response?.config?.url !== 'token/' &&
                    error.response?.status === 401)) &&
            !originalRequest._retry
        ) {
            if (isRefreshTokenFailed) {
                store.dispatch({ type: 'login/logout' });
                window.location.pathname = RoutePaths.login;
            } else if (isRefreshing) {
                try {
                    const token = await new Promise((resolve) => {
                        subscribeTokenRefresh(resolve);
                    });
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return instance(originalRequest);
                } catch (refreshError) {
                    return Promise.reject(refreshError);
                }
            }

            originalRequest._retry = true;
            isRefreshing = true;

            const refreshToken = getRefreshToken();
            try {
                const response = await instance.post(refreshUrl, {
                    refresh: refreshToken,
                });
                const { access: newToken } = response.data;
                saveTokens(newToken, refreshToken || '');
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                onTokenRefreshed(newToken);
                return instance(originalRequest);
            } catch (refreshError) {
                // Handle refresh token failure
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }
        return Promise.reject(error);
    }
);

export default instance;
